/**
 * Catalog of available theme presets.
 *
 * Each preset is just an id that maps to a `[data-theme="<id>"]` selector
 * defined in `globals.css`. The catalog metadata drives the demo switcher
 * UI — the `swatch` array is rendered as a small color preview, the
 * `description` is shown as flavor copy.
 */

export type ThemeId = "slate" | "sunset" | "forest";

export interface ThemePreset {
  id: ThemeId;
  name: string;
  description: string;
  vibe: string;
  /** HSL color values for visual preview swatches. */
  swatch: { background: string; surface: string; primary: string; accent: string };
}

export const THEME_PRESETS: ThemePreset[] = [
  {
    id: "slate",
    name: "Slate",
    description: "Clean, professional, neutral. The default OneShift look.",
    vibe: "Trusted · Modern",
    swatch: {
      background: "hsl(220 25% 98%)",
      surface: "hsl(0 0% 100%)",
      primary: "hsl(222 47% 22%)",
      accent: "hsl(222 80% 60%)",
    },
  },
  {
    id: "sunset",
    name: "Sunset",
    description: "Warm terracotta and cream. Energetic hospitality.",
    vibe: "Inviting · Energetic",
    swatch: {
      background: "hsl(30 14% 97%)",
      surface: "hsl(0 0% 100%)",
      primary: "hsl(14 82% 44%)",
      accent: "hsl(28 88% 48%)",
    },
  },
  {
    id: "forest",
    name: "Forest",
    description: "OneShift brand: forest green, cream, and yellow.",
    vibe: "On-brand · Grounded",
    swatch: {
      background: "hsl(37 32% 96%)",
      surface: "hsl(0 0% 100%)",
      primary: "hsl(152 63% 15%)",
      accent: "hsl(47 100% 60%)",
    },
  },
];

export const DEFAULT_THEME: ThemeId = "forest";

export function getThemePreset(id: ThemeId): ThemePreset {
  return THEME_PRESETS.find((p) => p.id === id) ?? THEME_PRESETS[0];
}
